import { __rest } from 'tslib';
import React, { useRef, useImperativeHandle, useEffect } from 'react';
import ReactSelect, { createFilter, components } from 'react-select';
import Creatable from 'react-select/creatable';
import { StyledSelectGlobal, StyledSelect } from './Select.styles.js';

const Select = React.forwardRef((props, ref) => {
  const {
      dataLwtId,
      isCreatable = false,
      styles,
      matchFrom,
      closeMenuOnSelect = true,
      hideSelectedOptions = false,
      isClearable = true,
      isDisabled = false,
      isLoading = false,
      isMulti = false,
      isSearchable = true,
      components: components$1,
      className,
      size = 'md',
      validationType,
      truncateOptions,
      disableKeyboardOnMobile = false,
      variant = 'standard',
      showPenultimateIcon = false
    } = props,
    restProps = __rest(props, ["dataLwtId", "isCreatable", "styles", "matchFrom", "closeMenuOnSelect", "hideSelectedOptions", "isClearable", "isDisabled", "isLoading", "isMulti", "isSearchable", "components", "className", "size", "validationType", "truncateOptions", "disableKeyboardOnMobile", "variant", "showPenultimateIcon"]);
  const selectRef = useRef(null);
  const selectProps = Object.assign({
    closeMenuOnSelect,
    hideSelectedOptions,
    isClearable,
    isDisabled,
    isLoading,
    isMulti,
    isSearchable
  }, restProps);
  const customStyles = styles || {
    control: (base, state) => Object.assign(Object.assign({}, base), {
      color: state.isFocused ? '#495057' : base.color,
      backgroundColor: state.isFocused ? '#fff' : base.backgroundColor,
      borderColor: state.isFocused ? '#5dbaed' : base.borderColor,
      outline: state.isFocused ? 0 : base.outline,
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(20, 125, 183, 0.25)' : base.boxShadow,
      '&:hover': {
        borderColor: state.isFocused ? '#5dbaed' : base.borderColor
      }
    }),
    menu: base => Object.assign(Object.assign({}, base), {
      zIndex: 201
    })
  };
  const getMatchFrom = () => {
    if (matchFrom === 'start') {
      return {
        filterOption: createFilter({
          matchFrom
        })
      };
    }
    return {};
  };
  const IndicatorSeparator = () => {
    return null;
  };
  const DropdownIndicator = props => {
    return React.createElement(components.DropdownIndicator, Object.assign({}, props), React.createElement("svg", {
      width: '20',
      height: '20',
      viewBox: '0 0 20 20',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg'
    }, React.createElement("path", {
      fillRule: 'evenodd',
      clipRule: 'evenodd',
      // eslint-disable-next-line max-len
      d: 'M5.46967 7.46967C5.76256 7.17678 6.23744 7.17678 6.53033 7.46967L10 10.9393L13.4697 7.46967C13.7626 7.17678 14.2374 7.17678 14.5303 7.46967C14.8232 7.76256 14.8232 8.23744 14.5303 8.53033L10.5303 12.5303C10.3897 12.671 10.1989 12.75 10 12.75C9.80109 12.75 9.61032 12.671 9.46967 12.5303L5.46967 8.53033C5.17678 8.23744 5.17678 7.76256 5.46967 7.46967Z',
      fill: '#4B5563'
    })));
  };
  const ClearIndicator = props => {
    return React.createElement(components.ClearIndicator, Object.assign({}, props), React.createElement("svg", {
      width: '20',
      height: '20',
      viewBox: '0 0 20 20',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg'
    }, React.createElement("path", {
      d: 'M5 5L10 10M10 10L15 15M10 10L5 15M10 10L15 5',
      stroke: '#4B5563',
      strokeWidth: '1.5',
      strokeLinecap: 'round',
      strokeLinejoin: 'round'
    })));
  };
  const MultiValueRemove = props => {
    return React.createElement(components.MultiValueRemove, Object.assign({}, props), React.createElement("svg", {
      width: '20',
      height: '20',
      viewBox: '0 0 20 20',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg'
    }, React.createElement("path", {
      fillRule: 'evenodd',
      clipRule: 'evenodd',
      // eslint-disable-next-line max-len
      d: 'M10 16C13.3137 16 16 13.3137 16 10C16 6.68629 13.3137 4 10 4C6.68629 4 4 6.68629 4 10C4 13.3137 6.68629 16 10 16ZM8.54881 7.47082C8.25656 7.17729 7.78168 7.17626 7.48815 7.46852C7.19463 7.76078 7.19359 8.23565 7.48585 8.52918L8.94164 9.9913L7.46852 11.4708C7.17626 11.7644 7.17729 12.2392 7.47082 12.5315C7.76435 12.8237 8.23922 12.8227 8.53148 12.5292L10 11.0543L11.4685 12.5292C11.7608 12.8227 12.2356 12.8237 12.5292 12.5315C12.8227 12.2392 12.8237 11.7644 12.5315 11.4708L11.0584 9.9913L12.5141 8.52918C12.8064 8.23565 12.8054 7.76078 12.5118 7.46852C12.2183 7.17626 11.7434 7.17729 11.4512 7.47082L10 8.92833L8.54881 7.47082Z',
      fill: '#6B7280'
    })));
  };
  const CustomOption = props => {
    var _a, _b;
    const children = showPenultimateIcon && ((_b = (_a = props.children) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.children) || props.children;
    return React.createElement(components.Option, Object.assign({}, props), React.createElement("div", {
      className: 'helix-select__option-inner helix-d-flex helix-align--center helix-gap-1'
    }, React.createElement("div", {
      className: 'helix-select__option-label'
    }, showPenultimateIcon ? React.Children.toArray(children).slice(0, -1) : children), props.isSelected && React.createElement("svg", {
      width: '20',
      height: '20',
      viewBox: '0 0 20 20',
      fill: 'none',
      xmlns: 'http://www.w3.org/2000/svg'
    }, React.createElement("path", {
      fillRule: 'evenodd',
      clipRule: 'evenodd',
      // eslint-disable-next-line max-len
      d: 'M15.9962 4.93763C16.3068 5.21168 16.3364 5.68563 16.0624 5.99623L8.56238 14.4962C8.4254 14.6515 8.23034 14.7432 8.0234 14.7496C7.81647 14.7561 7.61607 14.6767 7.46967 14.5303L3.96967 11.0303C3.67678 10.7374 3.67678 10.2626 3.96967 9.96968C4.26256 9.67679 4.73744 9.67679 5.03033 9.96968L7.96582 12.9052L14.9376 5.00379C15.2117 4.6932 15.6856 4.66358 15.9962 4.93763Z',
      fill: '#2563EB'
    })), showPenultimateIcon && React.Children.toArray(children).pop()));
  };
  const replacedComponents = Object.assign({
    IndicatorSeparator,
    DropdownIndicator,
    ClearIndicator,
    MultiValueRemove,
    Option: CustomOption
  }, components$1);
  const sizeClass = () => {
    switch (size) {
      case 'sm':
        return 'helix-select--sm';
      case 'lg':
        return 'helix-select--lg';
      default:
        return '';
    }
  };
  const variantClass = () => {
    switch (variant) {
      case 'filled':
        return 'helix-select--filled';
      default:
        return '';
    }
  };
  const classNames = `helix-select__wrapper ${sizeClass()} ${variantClass()} ${className ? className : ''}`;
  useImperativeHandle(ref, () => selectRef && selectRef.current);
  useEffect(() => {
    const isMobile = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (!selectRef.current) return;
    const input = selectRef.current.inputRef;
    if (input && isMobile && disableKeyboardOnMobile) {
      input.setAttribute('readonly', 'readonly');
    }
  }, [selectRef, disableKeyboardOnMobile]);
  const select = isCreatable ? React.createElement(Creatable, Object.assign({
    styles: customStyles
  }, getMatchFrom(), {
    classNamePrefix: 'helix-select',
    components: replacedComponents,
    ref: selectRef
  }, selectProps)) : React.createElement(ReactSelect, Object.assign({
    styles: customStyles
  }, getMatchFrom(), {
    classNamePrefix: 'helix-select',
    components: replacedComponents,
    ref: selectRef
  }, selectProps));
  return React.createElement(React.Fragment, null, React.createElement(StyledSelectGlobal, {
    truncateOptions: truncateOptions
  }), React.createElement(StyledSelect, {
    "data-lwt-id": dataLwtId,
    className: classNames.trimEnd(),
    validationType: validationType
  }, select));
});
Select.displayName = 'Select';

export { Select as default };
//# sourceMappingURL=Select.js.map
